import React from 'react';
import { useTranslation } from 'react-i18next';
import { IoPencil, IoTrashOutline, IoMoveOutline, IoPeopleOutline } from 'react-icons/io5';
import { MobileSheet } from '../../components/MobileSheet';

export type CalendarActionTarget =
  | {
      kind: 'metric';
      uniqueCode: string;
      label: string;
      dateParam: string;
    }
  | {
      kind: 'health_document';
      id: number;
      label: string;
      uniqueCode?: string | null;
      dateParam: string;
    }
  | {
      kind: 'event';
      id: number;
      label: string;
      dateParam: string;
    }
  | {
      kind: 'note';
      id: number;
      label: string;
      dateParam: string;
    }
  | {
      kind: 'workout';
      id: number;
      title: string;
      dateParam: string;
    };

interface Props {
  target: CalendarActionTarget | null;
  onClose: () => void;
  onDelete: (target: CalendarActionTarget) => void;
  onEdit?: (target: CalendarActionTarget) => void;
  onMove?: (target: CalendarActionTarget) => void;
  onCopyToAthletes?: (target: CalendarActionTarget) => void;
}

const MOVABLE_KINDS = new Set(['metric', 'health_document', 'event', 'note', 'workout']);

/** Bottom sheet after long-press on calendar items. */
export function CalendarItemActionSheet({ target, onClose, onDelete, onEdit, onMove, onCopyToAthletes }: Props) {
  const { t } = useTranslation();

  if (!target) return null;

  const title =
    target.kind === 'metric'
      ? t('calendar.actionSheet.metricTitle', 'Metric')
      : target.kind === 'health_document'
        ? t('calendar.healthDocument.cardLabel', 'Health report')
        : target.kind === 'event'
          ? t('calendar.actionSheet.eventTitle', 'Event')
          : target.kind === 'note'
            ? t('calendar.actionSheet.noteTitle', 'Note')
            : target.title;

  const subtitle =
    target.kind === 'workout'
      ? undefined
      : target.kind === 'metric' || target.kind === 'health_document'
        ? target.label
        : target.label;

  return (
    <MobileSheet open onClose={onClose} title={title} subtitle={subtitle}>
      <div className="mobile-choice-list">
        {target.kind === 'workout' && onCopyToAthletes && <button type="button" className="calendar-action-sheet__btn" onClick={() => { onCopyToAthletes(target); onClose(); }}><IoPeopleOutline size={18} aria-hidden /><span>{t('calendar.studio.copy.title')}</span></button>}
        {onEdit ? (
          <button
            type="button"
            className="calendar-action-sheet__btn"
            onClick={() => {
              onEdit(target);
              onClose();
            }}
          >
            <IoPencil size={18} aria-hidden />
            <span>{t('calendar.actionSheet.edit', 'Edit')}</span>
          </button>
        ) : null}
        {onMove && MOVABLE_KINDS.has(target.kind) ? (
          <button
            type="button"
            className="calendar-action-sheet__btn"
            onClick={() => {
              onMove(target);
              onClose();
            }}
          >
            <IoMoveOutline size={18} aria-hidden />
            <span>{t('calendar.actionSheet.move', 'Move')}</span>
          </button>
        ) : null}
        <button
          type="button"
          className="calendar-action-sheet__btn calendar-action-sheet__btn--danger"
          onClick={() => onDelete(target)}
        >
          <IoTrashOutline size={18} aria-hidden />
          <span>
            {target.kind === 'metric'
              ? t('calendar.actionSheet.deleteMetric', 'Delete metric')
              : target.kind === 'health_document'
                ? t('calendar.contextMenu.deleteHealthDocument', 'Delete health report')
                : target.kind === 'event'
                  ? t('calendar.actionSheet.deleteEvent', 'Delete event')
                  : target.kind === 'note'
                    ? t('calendar.actionSheet.deleteNote', 'Delete note')
                    : t('calendar.contextMenu.deleteWorkout', 'Delete workout')}
          </span>
        </button>
      </div>
    </MobileSheet>
  );
}
